import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { getAllProducts, updateProduct, deleteProduct } from '../services/productService';

const CATEGORIES = [
  'Tutti',
  'T-shirt',
  'Camicia',
  'Felpa',
  'Giacca',
  'Pantaloni',
  'Jeans',
  'Gonna',
  'Vestito',
  'Accessori',
  'Altro'
];

function AdminProductManager() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Tutti');

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllProducts();
      setProducts(data);
      setFilteredProducts(data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Errore nel caricamento prodotti', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    let filtered = products;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.barcode?.includes(q)
      );
    }

    if (categoryFilter !== 'Tutti') {
      filtered = filtered.filter(p =>
        p.category?.toLowerCase() === categoryFilter.toLowerCase()
      );
    }

    setFilteredProducts(filtered);
  }, [searchQuery, categoryFilter, products]);

  // Edit handlers
  const handleEditOpen = (product) => {
    setEditProduct(product);
    setEditForm({
      name: product.name || '',
      brand: product.brand || '',
      price: product.price || '',
      originalPrice: product.originalPrice || '',
      category: product.category || '',
      description: product.description || '',
      imageUrl: product.imageUrl || ''
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    try {
      setSaving(true);
      const updates = { ...editForm };
      if (updates.price) updates.price = parseFloat(updates.price);
      if (updates.originalPrice) updates.originalPrice = parseFloat(updates.originalPrice);
      else delete updates.originalPrice;

      await updateProduct(editProduct.id, updates);
      setSnackbar({ open: true, message: 'Prodotto aggiornato con successo', severity: 'success' });
      setEditOpen(false);
      await loadProducts();
    } catch (error) {
      setSnackbar({ open: true, message: 'Errore durante il salvataggio', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const handleDeleteOpen = (product) => {
    setDeleteTarget(product);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await deleteProduct(deleteTarget.id);
      setSnackbar({ open: true, message: 'Prodotto eliminato', severity: 'success' });
      setDeleteOpen(false);
      setDeleteTarget(null);
      await loadProducts();
    } catch (error) {
      setSnackbar({ open: true, message: 'Errore durante l\'eliminazione', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ backgroundColor: '#1a1a1a' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Gestione Prodotti
          </Typography>
          <Chip label={`${filteredProducts.length} prodotti`} color="primary" />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Search and Filter */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Cerca per nome, brand o barcode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: '200px', backgroundColor: 'white', borderRadius: '8px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
          <TextField
            select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            size="small"
            sx={{ minWidth: '150px', backgroundColor: 'white', borderRadius: '8px' }}
          >
            {CATEGORIES.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Products Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: '12px' }}>
            Nessun prodotto trovato
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {filteredProducts.map(product => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
                  }}
                >
                  {product.imageUrl && (
                    <CardMedia
                      component="img"
                      height="180"
                      image={product.imageUrl}
                      alt={product.name}
                      sx={{ objectFit: 'contain', backgroundColor: '#fafafa', p: 1 }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {product.name}
                    </Typography>
                    {product.brand && (
                      <Typography variant="body2" color="text.secondary">
                        {product.brand}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                        {'\u20AC'}{product.price}
                      </Typography>
                      {product.originalPrice && (
                        <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999' }}>
                          {'\u20AC'}{product.originalPrice}
                        </Typography>
                      )}
                    </Box>
                    {product.category && (
                      <Chip label={product.category} size="small" sx={{ mt: 1 }} />
                    )}
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditOpen(product)}
                      sx={{ color: '#667eea' }}
                    >
                      Modifica
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteOpen(product)}
                      color="error"
                    >
                      Elimina
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Modifica Prodotto</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome"
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Brand"
              value={editForm.brand || ''}
              onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Prezzo"
                type="number"
                value={editForm.price || ''}
                onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                fullWidth
              />
              <TextField
                label="Prezzo Originale"
                type="number"
                value={editForm.originalPrice || ''}
                onChange={(e) => setEditForm({ ...editForm, originalPrice: e.target.value })}
                fullWidth
              />
            </Box>
            <TextField
              label="Categoria"
              value={editForm.category || ''}
              onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
              fullWidth
            />
            <TextField
              label="Descrizione"
              value={editForm.description || ''}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="URL Immagine"
              value={editForm.imageUrl || ''}
              onChange={(e) => setEditForm({ ...editForm, imageUrl: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={saving}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' }
            }}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare <strong>{deleteTarget?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Questa azione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AdminProductManager;
